import { useEffect } from 'react';
import environment from '../environments/environment';

/**
 * Custom hook to dynamically manage PWA Manifest and Apple Meta Tags for Event Pages vs Dashboard
 *
 * @param {Object} params
 * @param {string} params.eventSlug - Current event slug (e.g. 'techsummit')
 * @param {string} [params.eventName] - Event display name
 * @param {string} [params.logo] - Custom logo URL or path
 * @param {string} [params.themeColor] - Event primary theme hex code (e.g. '#1A56DB')
 * @param {boolean} [params.isDashboard] - If true, strips manifest to prevent dashboard installation
 */
export function usePwaManifest({
  eventSlug,
  eventName = '',
  logo = '',
  themeColor = '#1A56DB',
  isDashboard = false,
}) {
  useEffect(() => {
    const head = document.head;

    // Helper: update or create link tag
    const setLinkTag = (rel, href, id) => {
      if (!href) return;
      let element = id ? document.getElementById(id) : head.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        if (id) element.id = id;
        head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Helper: update or create meta tag
    const setMetaTag = (name, content) => {
      if (!content) return;
      let element = head.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.name = name;
        head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper: remove element if exists
    const removeElement = (selector) => {
      const el = head.querySelector(selector);
      if (el) head.removeChild(el);
    };

    const DEFAULT_S3_FAVICON = 'https://eventzbook.s3.ap-south-1.amazonaws.com/favicon.ico';

    // CASE 1: Dashboard or non-event route -> Remove PWA manifest & event metadata
    if (isDashboard || !eventSlug) {
      removeElement('link[rel="manifest"]');
      removeElement('meta[name="apple-mobile-web-app-title"]');
      // Reset favicon & apple-touch-icon back to company logo512
      setLinkTag('icon', '/logo512.png', 'dynamic-favicon');
      setLinkTag('apple-touch-icon', '/logo512.png', 'dynamic-apple-touch-icon');
      // Reset theme color to default app color
      setMetaTag('theme-color', '#1A56DB');
      return;
    }

    // CASE 2: Public Event Website -> Inject dynamic event manifest, favicon & Apple meta tags
    const apiBase = environment.apiUrl || ''; // Backend base URL if cross-origin
    const frontendUrl = window.location.origin;
    const manifestUrl = `${apiBase}/manifest/${eventSlug}.webmanifest?frontendUrl=${encodeURIComponent(frontendUrl)}`;
    const iconUrl = `${apiBase}/manifest/icon/${eventSlug}`;

    // 1. Set PWA Manifest URL
    setLinkTag('manifest', manifestUrl, 'dynamic-pwa-manifest');

    // 2. Set Favicon & Apple-touch-icon for all browsers (Safari, Firefox, Chrome, Edge, Brave)
    setLinkTag('icon', iconUrl, 'dynamic-favicon');
    setLinkTag('apple-touch-icon', iconUrl, 'dynamic-apple-touch-icon');

    // 3. Set Theme Color
    setMetaTag('theme-color', themeColor || '#1A56DB');

    // 4. Apple-specific iOS PWA meta tags
    setMetaTag('apple-mobile-web-app-title', eventName || eventSlug);
    setMetaTag('apple-mobile-web-app-capable', 'yes');
    setMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');

    // 4. Update document title if eventName is present
    if (eventName) {
      document.title = eventName;
    }

    // Cleanup on unmount or slug change
    return () => {
      // Optional cleanup logic if needed
    };
  }, [eventSlug, eventName, logo, themeColor, isDashboard]);
}

export default usePwaManifest;
